import { operations } from "./openapi/generated-types";
import {
  createCompetition,
  createCompetitionGroup,
  createCompetitionMatchday,
  createPlayer,
  createTeam,
  enrollTeam,
  planGame,
} from "./openapi/api-client";
import { v4 as uuid } from "uuid";
import { DateTime } from "luxon";
import { players } from "./fwc-2026-players.ts";
import sleep from "sleep-promise";

type CreateTeam =
  operations["team.write.create"]["requestBody"]["content"]["application/json"];
type CreateTurnier =
  operations["competition.write.create"]["requestBody"]["content"]["application/json"];
type CreateMatchday =
  operations["competition.write.create-matchday"]["requestBody"]["content"]["application/json"];
type CreateGroup =
  operations["competition.write.create-group"]["requestBody"]["content"]["application/json"];
type PlanGame =
  operations["game.write.plan"]["requestBody"]["content"]["application/json"];

export const setupFWC2026 = async () => {
  // turnier
  const fwc: CreateTurnier = {
    id: "8065fb8f-b0a9-4c65-ae6c-3ba6a32d180e",
    name: "FreewayCup 2026",
    start: DateTime.fromISO("2026-01-17").toISO(),
    end: DateTime.fromISO("2026-01-18").toISO(),
    location: "Merkur-Arena Lübbecke",
    logoUrl:
      "https://app.freewaycup.de/app-assets/competitionlogo/freewaycup-blau.svg",
    settings: {
      topscorerCountAssists: true,
    },
  };
  await createCompetition(fwc);

  // teams
  const fwcTeams: Record<
    | "mka"
    | "fcb"
    | "fcu"
    | "bsc"
    | "koe"
    | "bmg"
    | "boc"
    | "s04"
    | "svw"
    | "hsv"
    | "bvb"
    | "m05"
    | "rbl"
    | "b04"
    | "vfb"
    | "dsc",
    CreateTeam
  > = {
    mka: {
      id: "5701917b-6997-4127-b66c-1d4e6d517994",
      name: "Mühlenkreis",
      shortName: "MKA",
      primaryColor: "#004f90",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/muehlenkreisauswahl.svg",
      sponsor: {
        name: "Volksbank PLUS",
        url: "https://www.vbplus.de",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/vb-plus.svg",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/vb-plus.jpg",
        // todo: jahreszahl muss aktualisiert werden
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/muehlenkreisauswahl.jpg",
        description: "",
      },
    },
    bsc: {
      id: "2dfb964c-e7e4-46f2-a060-394ca6eba972",
      name: "Hertha BSC Berlin",
      mediumName: "Hertha BSC",
      shortName: "BSC",
      primaryColor: "#004d9e",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/hertha-bsc-berlin.svg",
      sponsor: {
        name: "Bäckerei Schmidt",
        url: "https://baeckerei-schmidt.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/baeckerei-schmidt.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/baeckerei-schmidt.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/hertha-bsc-berlin.jpg",
        description: "",
      },
      staff:
        "Alexander Arsovic (Chef-Trainer), Damir Bektic (Co-Trainer), Davide Nardozza (Physiotherapeut), Johanna Vehlow (Sportpsychologin)",
    },
    rbl: {
      id: "7004db4b-e8c5-4e5a-aaf7-48f841b9f40a",
      name: "RB Leipzig",
      shortName: "RBL",
      primaryColor: "#DD013F",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/rb-leipzig.svg",
      sponsor: {
        name: "LSM event & messeservice",
        url: "https://www.lsm-gmbh.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/lsm.svg",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/lsm.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/rb-leipzig.jpg",
        description: "",
      },
      staff:
        "Dennis Bockelmann (Cheftrainer), Alexander Portleroy (Co-Trainer), Mika Moritz (Analyst), Jonas Bregler (Physiotherapeut)",
    },
    m05: {
      id: "6d32bda8-fb2b-49cc-b2fa-9750905965b4",
      name: "1. FSV Mainz 05",
      mediumName: "Mainz",
      shortName: "M05",
      primaryColor: "#C3141E",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/1-fsv-mainz-05.svg",
      sponsor: {
        name: "Plümat",
        url: "https://www.pluemat.info/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/pluemat.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/pluemat.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/1-fsv-mainz-05.jpg",
        description: "",
      },
      staff:
        "Yannick Dreyer (Trainer), Alexander Schraut (Co-Trainer), Carmen Müller (Physiotherapeutin), Jens Leydecker (Teammanager/Betreuer), Norman Haas (Athletiktrainer)",
    },
    dsc: {
      id: "c049705d-fa24-4ddb-969e-e8a8673cd62d",
      name: "DSC Arminia Bielefeld",
      mediumName: "Bielefeld",
      shortName: "DSC",
      primaryColor: "#004e95",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/dsc-arminia-bielefeld.svg",
      sponsor: {
        name: "Schulz Gebäudeservice",
        url: "https://www.sgbs.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/schulz.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/schulz.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/dsc-arminia-bielefeld.jpg",
        description: "",
      },
      staff:
        "Christoph Stork (Trainer), Fin Husemann (Co-Trainer), Helena Keles (Physiotherapeutin), Till Niermann (Athletiktrainer), Serhii Zaloha (Betreuer), Max Hoppe (Praktikant Athletik)",
    },
    vfb: {
      id: "20829cb9-e3ea-4cee-b717-6a45bffe2cea",
      name: "VfB Stuttgart",
      mediumName: "Stuttgart",
      shortName: "VfB",
      primaryColor: "#E32219",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/vfb-stuttgart.svg",
      sponsor: {
        name: "HARTING",
        url: "https://www.harting.com/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/harting.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/harting.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/vfb-stuttgart.jpg",
        description: "",
      },
      staff:
        "Andreas Weinberger (Trainer), Alex Albrecht (Co-Trainer), Marco Bräuninger (Athletiktrainer), Fabian Becker (Physiotherapeut)",
    },
    koe: {
      id: "fbb15df4-a2aa-4f62-a79c-c3d762a4a226",
      name: "1. FC Köln",
      mediumName: "Köln",
      shortName: "KOE",
      primaryColor: "#ED1C24",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/1-fc-koeln.svg",
      sponsor: {
        name: "terra",
        url: "https://www.wortmann.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/terra.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/terra.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/1-fc-koeln.jpg",
        description: "",
      },
      staff:
        "Manuel Hartmann (Cheftrainer), Tim Strenger (Co-Trainer), Felix Schückler (Physiotherapeut), Burak Ayhan (Betreuer)",
    },
    s04: {
      id: "fe9f4173-48ed-4bb8-97bf-2832cf9ca0c6",
      name: "FC Schalke 04",
      mediumName: "Schalke",
      shortName: "S04",
      primaryColor: "#004d9d",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/fc-schalke-04.svg",
      sponsor: {
        name: "Tempton Personaldienstleistungen",
        url: "https://www.tempton.de/standorte/buende",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/tempton.svg",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/tempton.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/fc-schalke-04.jpg",
        description: "",
      },
      staff:
        "Willi Landgraf (Trainer), Mats Daniel (Co-Trainer), Bekim Gerxhaliu (Teammanager), Miriam Pittig (Physiotherapeutin)",
    },
    bvb: {
      id: "23f97164-d70b-459a-846f-3d0a23802462",
      name: "Borussia Dortmund",
      mediumName: "Dortmund",
      shortName: "BVB",
      primaryColor: "#FDE100",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/borussia-dortmund.svg",
      sponsor: {
        name: "Home Deluxe",
        url: "https://www.homedeluxe.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/home-deluxe.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/home-deluxe.png",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/borussia-dortmund.jpg",
        description: "",
      },
      staff:
        "Karsten Gorges (Trainer), Niklas Bühler (Co-Trainer), Simon Engelberger-Semper (Analyst), Jan Reckert (Torwarttrainer), Goran Pantic (Physiotherapeut), Sebastian Skrzipczyk (Betreuer), Sebastian Hille (Assistent)",
    },
    svw: {
      id: "91fcd045-c8b7-4eb9-9a08-7270366df90e",
      name: "SV Werder Bremen",
      mediumName: "Werder Bremen",
      shortName: "SVW",
      primaryColor: "#1D9053",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/werder-bremen.svg",
      sponsor: {
        name: "Fahrschule Delil",
        url: "https://fahrschule-delil.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/fahrschule-delil.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/fahrschule-delil.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/werder-bremen.jpg",
        description: "",
      },
      staff:
        "Finn Jaensch (Trainer), Niclas Bergmann (Co-Trainer), Börge Brünjes (Co-Trainer), Oliver Bastian (Betreuer), Luca Hinz (Physiotherapeut), Constantin Kuhlmann (Sportpsychologe)",
    },
    fcb: {
      id: "2cf68047-ff20-4eda-8775-e708c633c974",
      name: "FC Bayern München",
      mediumName: "FC Bayern",
      shortName: "FCB",
      primaryColor: "#DC052D",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/fc-bayern-muenchen.svg",
      sponsor: {
        name: "Schneider Gastronomie & Catering",
        url: "https://www.schneider-gc.de",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/schneider-gc.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/schneider-gc.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/fc-bayern-muenchen.jpg",
        description: "",
      },
      staff:
        "Sebastian Dreier (Cheftrainer), Thomas Kurz (Co-Trainer), Björn Bauer (Physiotherapeut), Janne Mlinarsky (Betreuerin), Yannick Wloka (Teammanager), Maximilian Straub (Sportlicher Koordinator)",
    },
    b04: {
      id: uuid(),
      name: "Bayer 04 Leverkusen",
      mediumName: "Leverkusen",
      shortName: "B04",
      primaryColor: "#E32221",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/bayer-04-leverkusen.svg",
      sponsor: {
        name: "Autohaus Schmale",
        url: "https://www.autohaus-schmale.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/autohaus-schmale.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/autohaus-schmale.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/bayer-04-leverkusen.jpg",
        description: "",
      },
      staff:
        "Andreas Bona (Cheftrainer), Sören Schuschke (Co-Trainer), Thomas Huber (Co-Trainer/Teambetreuer), King-Midas Ntem (Physiotherapeut)",
    },
    fcu: {
      id: "8bb71f70-714a-47bc-9a83-8233bec06c28",
      name: "Union Berlin",
      shortName: "FCU",
      primaryColor: "#EB1923",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/1-fc-union-berlin.svg",
      sponsor: {
        name: "Union Investment",
        url: "https://www.union-investment.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/union-investment.svg",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/union-investment.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/1-fc-union-berlin.jpg",
        description: "",
      },
      staff:
        "Marco Gebhardt (Trainer), Martin Schmitt (Co-Trainer), Max Köhler (Torwarttrainer), Taira Miamoto",
    },
    hsv: {
      id: "456029a4-98e0-4180-a3e5-e429bde658dc",
      name: "Hamburger SV",
      mediumName: "HSV",
      shortName: "HSV",
      primaryColor: "#0A3F86",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/hamburger-sv.svg",
      sponsor: {
        name: "Naue Fasertechnik",
        url: "https://www.naue.com/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/naue.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/naue.png",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/hamburger-sv.jpg",
        description: "",
      },
      staff:
        "Sören Bertram (Co-Trainer), Stefan Knop (Physiotherapeut), Fritz Vosen (Co-Trainer), Luca Zampolin (Trainer), Kurosh Niakan (Physiotherapeut)",
    },
    boc: {
      id: "256cdf47-0e9e-4b46-ba57-8913ba186d14",
      name: "VfL Bochum",
      mediumName: "Bochum",
      shortName: "BOC",
      primaryColor: "#005CA9",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/vfl-bochum.svg",
      sponsor: {
        name: "chargecats",
        url: "https://chargecats.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/chargecats.svg",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/chargecats.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/vfl-bochum.jpg",
        description: "",
      },
      staff:
        "Dennis Gieseker (Trainer), Sven Kaiser (Co-Trainer), Michael Krupke (Teammanager), Diyar Gülaydin (Physiotherapeut)",
    },
    bmg: {
      id: "8feaf5af-553b-46f9-ab11-50992bbcd1b4",
      name: "Borussia Mönchengladbach",
      mediumName: "M'gladbach",
      shortName: "BMG",
      primaryColor: "#000000",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/teamlogo/borussia-moenchengladbach.svg",
      sponsor: {
        name: "Special Trends",
        url: "https://special-trends.de/",
        logoFilePath:
          "https://app.freewaycup.de/app-assets/sponsorlogo/special-trends.png",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/special-trends.jpg",
      },
      photo: {
        filePath:
          "https://app.freewaycup.de/app-assets/teamphoto/2026/fwc/borussia-moenchengladbach.jpg",
        description: "",
      },
      staff:
        "Felix Galli (Trainer), Tae Young Moon (Co-Trainer), Martin Kompalla (Torwarttrainer), Sascha Kreutzer (Physiotherapeut)",
    },
  };

  const createTeams = Object.values(fwcTeams).map(async (t) => {
    await createTeam(t);
    await enrollTeam({
      id: fwc.id,
      teamId: t.id,
    });
  });
  await Promise.all(createTeams);

  // group
  const groupVorrunde: CreateGroup = {
    id: fwc.id,
    groupId: "cef7f28a-1d50-430f-ae20-4b06ba4d1997",
    name: "Vorrunde",
    teamIds: Object.values(fwcTeams).map((t) => t.id),
  };
  await createCompetitionGroup(groupVorrunde);

  // matchdays
  const matchdays: Record<
    | "vorrunde"
    | "achtelfinale"
    | "viertelfinale"
    | "halbfinale"
    | "kleinesfinale"
    | "finale",
    CreateMatchday
  > = {
    vorrunde: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Vorrunde",
    },
    achtelfinale: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Achtelfinale",
    },
    viertelfinale: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Viertelfinale",
    },
    halbfinale: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Halbfinale",
    },
    kleinesfinale: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Kleines Finale",
    },
    finale: {
      id: fwc.id,
      matchdayId: uuid(),
      name: "Finale",
    },
  };

  await createCompetitionMatchday(matchdays.vorrunde);
  await createCompetitionMatchday(matchdays.achtelfinale);
  await createCompetitionMatchday(matchdays.viertelfinale);
  await createCompetitionMatchday(matchdays.halbfinale);
  await createCompetitionMatchday(matchdays.kleinesfinale);
  await createCompetitionMatchday(matchdays.finale);

  // games
  const gamesVorrunde: PlanGame[] = [
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 01",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.dsc.id,
        },
        guest: {
          teamId: fwcTeams.fcb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T12:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 02",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.boc.id,
        },
        guest: {
          teamId: fwcTeams.vfb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T12:15:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 03",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.mka.id,
        },
        guest: {
          teamId: fwcTeams.m05.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T12:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 04",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bvb.id,
        },
        guest: {
          teamId: fwcTeams.rbl.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T12:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 05",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bmg.id,
        },
        guest: {
          teamId: fwcTeams.bsc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T13:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 06",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.s04.id,
        },
        guest: {
          teamId: fwcTeams.svw.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T13:15:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 07",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.b04.id,
        },
        guest: {
          teamId: fwcTeams.fcu.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T13:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 08",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.koe.id,
        },
        guest: {
          teamId: fwcTeams.hsv.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T13:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 09",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.dsc.id,
        },
        guest: {
          teamId: fwcTeams.bsc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T14:15:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 10",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.boc.id,
        },
        guest: {
          teamId: fwcTeams.svw.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T14:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 11",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bvb.id,
        },
        guest: {
          teamId: fwcTeams.fcu.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T14:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 12",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.mka.id,
        },
        guest: {
          teamId: fwcTeams.hsv.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T15:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 13",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.rbl.id,
        },
        guest: {
          teamId: fwcTeams.b04.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T15:15:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 14",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.vfb.id,
        },
        guest: {
          teamId: fwcTeams.bmg.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T15:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 15",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.fcb.id,
        },
        guest: {
          teamId: fwcTeams.koe.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T15:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 16",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.m05.id,
        },
        guest: {
          teamId: fwcTeams.s04.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T16:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 17",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.rbl.id,
        },
        guest: {
          teamId: fwcTeams.boc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T16:40:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 18",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.vfb.id,
        },
        guest: {
          teamId: fwcTeams.mka.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T16:55:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 19",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.fcb.id,
        },
        guest: {
          teamId: fwcTeams.bvb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T17:10:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 20",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.m05.id,
        },
        guest: {
          teamId: fwcTeams.dsc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T17:25:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 21",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.b04.id,
        },
        guest: {
          teamId: fwcTeams.bsc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T17:40:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 22",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.koe.id,
        },
        guest: {
          teamId: fwcTeams.svw.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T17:55:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 23",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bmg.id,
        },
        guest: {
          teamId: fwcTeams.fcu.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T18:10:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 24",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.s04.id,
        },
        guest: {
          teamId: fwcTeams.hsv.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-17T18:25:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 25",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.b04.id,
        },
        guest: {
          teamId: fwcTeams.boc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T08:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 26",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.s04.id,
        },
        guest: {
          teamId: fwcTeams.bvb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T08:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 27",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.koe.id,
        },
        guest: {
          teamId: fwcTeams.dsc.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T09:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 28",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bmg.id,
        },
        guest: {
          teamId: fwcTeams.mka.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T09:15:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 29",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.bsc.id,
        },
        guest: {
          teamId: fwcTeams.vfb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T09:30:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 30",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.fcu.id,
        },
        guest: {
          teamId: fwcTeams.rbl.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T09:45:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 31",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.svw.id,
        },
        guest: {
          teamId: fwcTeams.m05.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T10:00:00").toISO(),
    },
    {
      id: uuid(),
      groupId: groupVorrunde.groupId,
      matchdayId: matchdays.vorrunde.matchdayId,
      name: "Spiel 32",
      competitionId: fwc.id,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          teamId: fwcTeams.hsv.id,
        },
        guest: {
          teamId: fwcTeams.fcb.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T10:15:00").toISO(),
    },
  ];

  const createGamesVorrunde = gamesVorrunde.map((g) => planGame(g));
  await Promise.all(createGamesVorrunde);

  const gamesAchtelfinale: Record<
    "g33" | "g34" | "g35" | "g36" | "g37" | "g38" | "g39" | "g40",
    PlanGame
  > = {
    g33: {
      id: uuid(),
      name: "Spiel 33",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 8,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 9,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T10:45:00").toISO(),
    },
    g34: {
      id: uuid(),
      name: "Spiel 34",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 7,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 10,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T11:00:00").toISO(),
    },
    g35: {
      id: uuid(),
      name: "Spiel 35",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 6,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 11,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T11:15:00").toISO(),
    },
    g36: {
      id: uuid(),
      name: "Spiel 36",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 5,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 12,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T11:30:00").toISO(),
    },
    g37: {
      id: uuid(),
      name: "Spiel 37",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 1,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 16,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T11:45:00").toISO(),
    },
    g38: {
      id: uuid(),
      name: "Spiel 38",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 2,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 15,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T12:00:00").toISO(),
    },
    g39: {
      id: uuid(),
      name: "Spiel 39",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 3,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 14,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T12:15:00").toISO(),
    },
    g40: {
      id: uuid(),
      name: "Spiel 40",
      competitionId: fwc.id,
      matchdayId: matchdays.achtelfinale.matchdayId,
      duration: 12 * 60,
      plannedTeams: {
        home: {
          groupId: groupVorrunde.groupId,
          position: 4,
        },
        guest: {
          groupId: groupVorrunde.groupId,
          position: 13,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T12:30:00").toISO(),
    },
  };

  const createGamesAchtelfinale = Object.values(gamesAchtelfinale).map((g) =>
    planGame(g),
  );
  await Promise.all(createGamesAchtelfinale);

  const gamesViertelfinale: Record<"g41" | "g42" | "g43" | "g44", PlanGame> = {
    g41: {
      id: uuid(),
      name: "Spiel 41",
      competitionId: fwc.id,
      matchdayId: matchdays.viertelfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesAchtelfinale.g33.id,
        },
        guest: {
          winnerTeam: gamesAchtelfinale.g37.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T13:00:00").toISO(),
    },
    g42: {
      id: uuid(),
      name: "Spiel 42",
      competitionId: fwc.id,
      matchdayId: matchdays.viertelfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesAchtelfinale.g34.id,
        },
        guest: {
          winnerTeam: gamesAchtelfinale.g38.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T13:20:00").toISO(),
    },
    g43: {
      id: uuid(),
      name: "Spiel 43",
      competitionId: fwc.id,
      matchdayId: matchdays.viertelfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesAchtelfinale.g36.id,
        },
        guest: {
          winnerTeam: gamesAchtelfinale.g40.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T13:40:00").toISO(),
    },
    g44: {
      id: uuid(),
      name: "Spiel 44",
      competitionId: fwc.id,
      matchdayId: matchdays.viertelfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesAchtelfinale.g35.id,
        },
        guest: {
          winnerTeam: gamesAchtelfinale.g39.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T14:00:00").toISO(),
    },
  };
  const createGamesViertelfinale = Object.values(gamesViertelfinale).map((g) =>
    planGame(g),
  );
  await Promise.all(createGamesViertelfinale);

  const gamesHalbfinale: Record<"g45" | "g46", PlanGame> = {
    g45: {
      id: uuid(),
      name: "Spiel 45",
      competitionId: fwc.id,
      matchdayId: matchdays.halbfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesViertelfinale.g41.id,
        },
        guest: {
          winnerTeam: gamesViertelfinale.g43.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T14:20:00").toISO(),
    },
    g46: {
      id: uuid(),
      name: "Spiel 46",
      competitionId: fwc.id,
      matchdayId: matchdays.halbfinale.matchdayId,
      duration: 15 * 60,
      plannedTeams: {
        home: {
          winnerTeam: gamesViertelfinale.g42.id,
        },
        guest: {
          winnerTeam: gamesViertelfinale.g44.id,
        },
      },
      dateTime: DateTime.fromISO("2026-01-18T14:40:00").toISO(),
    },
  };
  const createGamesHalbfinale = Object.values(gamesHalbfinale).map((g) =>
    planGame(g),
  );
  await Promise.all(createGamesHalbfinale);

  // spiel um platz 3
  await planGame({
    id: uuid(),
    name: "Spiel 47",
    competitionId: fwc.id,
    matchdayId: matchdays.kleinesfinale.matchdayId,
    duration: 15 * 60,
    plannedTeams: {
      home: {
        loserTeam: gamesHalbfinale.g45.id,
      },
      guest: {
        loserTeam: gamesHalbfinale.g46.id,
      },
    },
    dateTime: DateTime.fromISO("2026-01-18T15:20:00").toISO(),
  });

  // finale
  await planGame({
    id: uuid(),
    name: "Spiel 48",
    competitionId: fwc.id,
    matchdayId: matchdays.finale.matchdayId,
    duration: 15 * 60,
    plannedTeams: {
      home: {
        winnerTeam: gamesHalbfinale.g45.id,
      },
      guest: {
        winnerTeam: gamesHalbfinale.g46.id,
      },
    },
    dateTime: DateTime.fromISO("2026-01-18T15:45:00").toISO(),
  });

  // azubi cup
  const azubicup: CreateTurnier = {
    id: "6795ae65-bee8-422c-bb73-fefb729a0ae7",
    name: "HARTING Azubi-Cup 2026",
    start: DateTime.fromISO("2026-01-17").toISO(),
    end: DateTime.fromISO("2026-01-17").toISO(),
    location: "Merkur-Arena Lübbecke",
    settings: {
      topscorerCountAssists: true,
    },
  };
  await createCompetition(azubicup);

  await sleep(5);

  const azubicupMatchday: CreateMatchday = {
    id: azubicup.id,
    matchdayId: uuid(),
    name: "Finale",
  };
  await createCompetitionMatchday(azubicupMatchday);

  const azubicupTeams: Record<"mer" | "het", CreateTeam> = {
    mer: {
      id: uuid(),
      name: "MERKUR.COM AG",
      shortName: "MER",
      primaryColor: "#022652",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/sponsorlogo/merkur-sun.svg",
      sponsor: {
        name: "MERKUR.COM AG",
        url: "https://www.merkur.com",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/azubicup.jpg",
      },
    },
    het: {
      id: uuid(),
      name: "Hettich",
      shortName: "HET",
      primaryColor: "#42A9D6",
      logoFilePath:
        "https://app.freewaycup.de/app-assets/sponsorlogo/hettich.svg",
      sponsor: {
        name: "Hettich",
        url: "https://www.hettich.com/",
        ledwallFilePath:
          "https://app.freewaycup.de/app-assets/ledwall/sponsors/$widthx$height/azubicup.jpg",
      },
    },
  };

  const createAzubicupTeams = Object.values(azubicupTeams).map(async (t) => {
    await createTeam(t);
    await enrollTeam({
      id: azubicup.id,
      teamId: t.id,
    });
  });
  await Promise.all(createAzubicupTeams);

  // finale
  await planGame({
    id: uuid(),
    name: "Finale",
    competitionId: azubicup.id,
    matchdayId: azubicupMatchday.matchdayId,
    duration: 15 * 60,
    plannedTeams: {
      home: {
        teamId: azubicupTeams.mer.id,
      },
      guest: {
        teamId: azubicupTeams.het.id,
      },
    },
    dateTime: DateTime.fromISO("2026-01-17T16:15:00").toISO(),
  });

  const createAllPlayers = players.map((p) => {
    return createPlayer({
      id: uuid(),
      firstName: p.firstName,
      lastName: p.lastName,
      isGoalkeeper: p.isGoalkeeper,
      number: p.number,
      teamId:
        fwcTeams[p.teamShortName]?.id ??
        azubicupTeams[p.teamShortName]?.id ??
        "",
      birthday: `${p.birthday}T00:00:00+01:00`,
    });
  });

  await Promise.all(createAllPlayers);
};
